import { Brand } from '@global/infrastructure/enums/brand-type.enum';
import { Environment as IEnvironment } from '@global/infrastructure/models/environment';

export enum Environment {
  Dev = 'dev',
  Qa = 'qa',
  Preprod = 'preprod',
  Prod = 'prod',
}

export abstract class EnvironmentProviderConfig {
  private static readonly nonProdApiKey = process.env['NG_APP_NON_PROD_API_KEY'];
  private static readonly prodApiKey = process.env['NG_APP_PROD_API_KEY'];
  private static readonly nonProdRecaptchaKey = process.env['NG_APP_NON_PROD_RECAPTCHA_KEY'];
  private static readonly prodRecaptchaKey = process.env['NG_APP_PROD_RECAPTCHA_KEY'];
  private static readonly prodHumanId = 'PXuH2dJvgZ';
  private static readonly nonProdHumanId = 'PXL25WnbYu';

  private static readonly configOverrides = {
    'b4c-uat-rc2-c11': {
      scripts: Environment.Qa,
      keys: Environment.Qa,
    },
    'b4c-nft-rc2-c11': {
      scripts: Environment.Qa,
      keys: Environment.Qa,
    },
    'b4c-mta-ci': {
      scripts: Environment.Qa,
      keys: Environment.Qa,
    },
    'b4c-mta-qa': {
      scripts: Environment.Qa,
      keys: Environment.Qa,
    },
    'b4c-mta-ua': {
      scripts: Environment.Qa,
      keys: Environment.Qa,
    },
    'b4c-pro-rc2-c11': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod,
    },
    'b4c-pro': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod,
    },
    'claims-preprod': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod,
    },
    'digital-preprod-dl': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod,
    },
    'digital-preprod-ch': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod,
    },
    'digital-preprod-pr': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod,
    },
    'digital-prod': {
      scripts: Environment.Prod,
      keys: Environment.Prod,
    },
    'directline-preprod': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'churchill-preprod': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'claims-preprod-ch': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'b4c-pro-pr': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'b4c-pro-rc2-c11-ch': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'b4c-pro-rc2-c11-dl': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'b4c-pro-rc2-c11-pr': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'b4c-pro-ch': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'b4c-pro-dl': {
      scripts: Environment.Preprod,
      keys: Environment.Preprod
    },
    'digital-prod-dl': {
      scripts: Environment.Prod,
      keys: Environment.Prod
    },
    'digital-prod-ch': {
      scripts: Environment.Prod,
      keys: Environment.Prod
    },
    'digital-prod-pr': {
      scripts: Environment.Prod,
      keys: Environment.Prod
    }
  };

  static getWindowLocation(): Location {
    return window.location;
  }

  public static readonly mockServerUrl =
    'https://motor-mocks-server.b4c-dev.dlgdigitalapi.com/api';

  private static readonly productionHostnameBrand = {
    'privilege.com': Brand.Privilege,
    'churchill.com': Brand.Churchill,
    'directline.com': Brand.DirectLine,
  };

  private static readonly shorthandBrandToBrand = {
    pr: Brand.Privilege,
    ch: Brand.Churchill,
    dl: Brand.DirectLine,
  };

  private static readonly keys = {
    [Environment.Dev]: {
      recaptchaSiteKey: this.nonProdRecaptchaKey,
      apiKey: this.nonProdApiKey,
    },
    [Environment.Qa]: {
      recaptchaSiteKey: this.nonProdRecaptchaKey,
      apiKey: this.nonProdApiKey,
    },
    [Environment.Preprod]: {
      recaptchaSiteKey: this.prodRecaptchaKey,
      apiKey: this.prodApiKey,
    },
    [Environment.Prod]: {
      recaptchaSiteKey: this.prodRecaptchaKey,
      apiKey: this.prodApiKey,
    },
  };

  private static readonly getScripts = (
    brand: Brand.DirectLine | Brand.Privilege | Brand.Churchill,
    env:
      | Environment.Dev
      | Environment.Qa
      | Environment.Preprod
      | Environment.Prod
  ): { tealiumScript: string; tealiumSync: string, humanScript: string, analyticsScript: string } => {
    return {
      tealiumScript: `https://tags.tiqcdn.com/utag/dlg/${brand}/${env}/utag.js`,
      tealiumSync: `https://tags.tiqcdn.com/utag/dlg/${brand}/${env}/utag.sync.js`,
      humanScript: `https://client.px-cloud.net/${env === Environment.Prod ? this.prodHumanId : this.nonProdHumanId}/main.min.js`,
      analyticsScript: '',
    };
  };

  // tslint:disable-next-line:member-ordering
  private static readonly defaultAppConfig = {
    ...EnvironmentProviderConfig.keys.dev,
    ...EnvironmentProviderConfig.getScripts(
      Brand.Privilege,
      Environment.Dev,
    ),
  };

  private static getEnvBrandFromHostname(hostname: string): {
    environment: string;
    brand: Brand;
  } {
    const subdomainSplit = hostname.split('.')[0]?.split('-');
    const shorthandBrandName = subdomainSplit.pop();

    return {
      environment: subdomainSplit.join('-'),
      brand:
        EnvironmentProviderConfig.shorthandBrandToBrand[shorthandBrandName],
    };
  }

  static generateConfig(enableMocks: Boolean): IEnvironment {
    const { hostname, origin } = this.getWindowLocation();

    const prodEnvironmentBrand =
      EnvironmentProviderConfig.productionHostnameBrand[
      hostname.replace('www.', '')
      ];
    const isProduction = !!prodEnvironmentBrand;

    const config = {
      ...EnvironmentProviderConfig.defaultAppConfig,
      production: isProduction,
      apiRouting: '',
      apiUrl: enableMocks
        ? EnvironmentProviderConfig.mockServerUrl
        : hostname === 'localhost'
          ? `http://localhost:${window.location.port}`
          : `${origin}/car/api/v1`,
    };

    if (isProduction) {
      return {
        ...config,
        ...EnvironmentProviderConfig.keys[Environment.Prod],
        ...EnvironmentProviderConfig.getScripts(
          prodEnvironmentBrand,
          Environment.Prod,
        ),
      };
    }

    const { brand, environment } =
      EnvironmentProviderConfig.getEnvBrandFromHostname(hostname);
    if (!!brand && !!environment) {
      const scriptsEnvironment =
        EnvironmentProviderConfig.configOverrides[environment]?.scripts ||
        Environment.Dev;
      const keysEnvironment =
        EnvironmentProviderConfig.configOverrides[environment]?.keys ||
        Environment.Dev;

      return {
        ...config,
        ...EnvironmentProviderConfig.getScripts(
          brand,
          scriptsEnvironment
        ),
        ...EnvironmentProviderConfig.keys[keysEnvironment],
      };
    }

    return config;
  }
}
